import * as z from 'zod'

/**
 * Single owner of the bulk ("apply to every selected link") vocabulary for the
 * admin Links table: what a bulk edit is allowed to change, how a selection plus
 * one set of chosen values becomes the single request body, and what the
 * endpoint answers with.
 *
 * `PATCH /partners/{id}/coupons/bulk/` takes the whole selection and a set of
 * deltas, so this module builds ONE body regardless of how many links are
 * selected. Two consequences shape the surface:
 *
 * - **Tags travel as deltas, never as a merged set.** `add_tag_ids` /
 *   `remove_tag_ids` are resolved per coupon against the live row on the server,
 *   so a tag another admin attaches between this page's load and the apply
 *   survives. The full-set `tag_ids` replace that the per-coupon PATCH exposes
 *   cannot offer that — it is computed from an already-stale read.
 * - **No-ops are the server's business.** A link that already matches is counted
 *   in `skipped`, never written and never failed, so nothing here filters the
 *   selection down first. `is_active` is sent as an absolute value; the server
 *   compares it against each row.
 *
 * `code` is deliberately not bulk-editable: codes are unique per link, so there
 * is no single value to apply across a selection.
 */

/** What a bulk edit may do to the selected links' active flag. */
export const COUPON_BULK_STATUS_ACTIONS = ['keep', 'activate', 'deactivate'] as const

export type CouponBulkStatusAction = (typeof COUPON_BULK_STATUS_ACTIONS)[number]

export type CouponBulkEditValues = {
  /** Tags to add to every selected link. Tags already present are untouched. */
  addTagIds: string[]
  /** Tags to strip from every selected link. Tags not present are untouched. */
  removeTagIds: string[]
  status: CouponBulkStatusAction
}

export const EMPTY_COUPON_BULK_EDIT: CouponBulkEditValues = {
  addTagIds: [],
  removeTagIds: [],
  status: 'keep',
}

/** The bulk endpoint's request body. Only the touched fields are sent. */
export type CouponBulkUpdateRequest = {
  coupon_ids: string[]
  is_active?: boolean
  add_tag_ids?: string[]
  remove_tag_ids?: string[]
}

/**
 * The endpoint's own cap (`maxItems: 500` on `coupon_ids`).
 *
 * The Links table cannot reach it today — a selection covers the loaded page and
 * the largest page the grid offers is 50 — but the ceiling is the endpoint's, not
 * the table's, so it is asserted at the transport rather than inferred from the
 * page size a future change could raise.
 */
export const COUPON_BULK_MAX_IDS = 500

/**
 * One coupon the server could not write. `code` is null when the id matched no
 * coupon for this partner — there is no code to report — so a caller showing
 * this to an admin needs its own label fallback.
 */
export const couponBulkFailureSchema = z.object({
  coupon_id: z.string(),
  code: z.string().nullable(),
  reason: z.string(),
})

export type CouponBulkFailure = z.infer<typeof couponBulkFailureSchema>

/**
 * The bulk endpoint's answer. Parsed rather than trusted, because it is the ONLY
 * thing that says whether the action landed: the call returns 200 even when
 * every coupon is in `failed`, so a caller reading the status line instead of
 * these three fields would report a total failure as a success.
 */
export const couponBulkResultSchema = z.object({
  /** Coupons whose state changed. */
  updated: z.number(),
  /** Coupons already in the requested state — counted, never written. */
  skipped: z.number(),
  failed: z.array(couponBulkFailureSchema),
})

export type CouponBulkResult = z.infer<typeof couponBulkResultSchema>

/**
 * What a finished bulk action did, as the UI needs to state it. `none` is its own
 * outcome rather than a quiet success: deactivating an already-inactive selection
 * is a 200 with `updated: 0`, and calling that "N links updated" would be a lie.
 */
export type CouponBulkOutcome =
  | { kind: 'none' }
  | { kind: 'success'; updated: number }
  | { kind: 'partial'; updated: number; failedCodes: string[] }

/** A coupon the caller had selected, for naming a failure the admin recognises. */
type LabelledCoupon = { id: string; code: string }

/**
 * The label to show for a failed link. The response's `code` is null when the id
 * matched no coupon for this partner (deleted under the admin's feet), so the
 * loaded row is the next best name and the raw id the last resort — printing
 * "null" into the alert is the one outcome to rule out.
 */
const failureLabel = (failure: CouponBulkFailure, selection: readonly LabelledCoupon[]): string =>
  failure.code ??
  selection.find((coupon) => coupon.id === failure.coupon_id)?.code ??
  failure.coupon_id

/**
 * Read the outcome off the response BODY. Lives here, beside the schema whose
 * comment explains why, because it is the invariant this whole transport exists
 * to get right: the endpoint answers 200 even when nothing was written, so any
 * caller deriving success from the resolved mutation would report a total
 * failure as a win.
 *
 * Branch order matters. `failed` is checked first — a partial failure outranks
 * both other readings. Only then can `updated === 0` mean "the server skipped
 * every link", because with no failures `updated + skipped` is the whole
 * selection.
 */
export const toCouponBulkOutcome = (
  result: CouponBulkResult,
  selection: readonly LabelledCoupon[],
): CouponBulkOutcome => {
  if (result.failed.length > 0) {
    return {
      kind: 'partial',
      updated: result.updated,
      failedCodes: result.failed.map((failure) => failureLabel(failure, selection)),
    }
  }

  return result.updated === 0 ? { kind: 'none' } : { kind: 'success', updated: result.updated }
}

/**
 * Whether the chosen values would change anything at all. Gates the dialog's
 * submit so "Apply" can never fire a request that asks for nothing.
 */
export const hasCouponBulkChange = (values: CouponBulkEditValues): boolean =>
  values.addTagIds.length > 0 || values.removeTagIds.length > 0 || values.status !== 'keep'

/**
 * Record the tags to add, withdrawing any of them from the removal side.
 *
 * A tag chosen on both sides would be added to every link and then stripped
 * again in the same apply — a request that cannot express what the admin meant.
 * Resolving it here rather than at submit time keeps the two pickers honest
 * about each other while they are still being filled in.
 */
export const chooseAddTags = (
  values: CouponBulkEditValues,
  addTagIds: string[],
): CouponBulkEditValues => ({
  ...values,
  addTagIds,
  removeTagIds: values.removeTagIds.filter((tagId) => !addTagIds.includes(tagId)),
})

/** The mirror of {@link chooseAddTags}: the latest choice wins. */
export const chooseRemoveTags = (
  values: CouponBulkEditValues,
  removeTagIds: string[],
): CouponBulkEditValues => ({
  ...values,
  removeTagIds,
  addTagIds: values.addTagIds.filter((tagId) => !removeTagIds.includes(tagId)),
})

/** `null` = leave the flag alone; otherwise the flag to write. */
const targetIsActive = (status: CouponBulkStatusAction): boolean | null => {
  switch (status) {
    case 'keep':
      return null
    case 'activate':
      return true
    case 'deactivate':
      return false
  }
}

/**
 * Turn a selection plus one set of chosen values into the bulk request body.
 *
 * Every field the edit did not touch is ABSENT rather than empty or null: the
 * endpoint reads an omitted `is_active` as "leave the flag alone" and an omitted
 * tag delta as "no tags to move". Sending `is_active` unasked would write the
 * flag on every selected link.
 */
export const buildCouponBulkRequest = (
  couponIds: string[],
  values: CouponBulkEditValues,
): CouponBulkUpdateRequest => {
  const nextIsActive = targetIsActive(values.status)

  return {
    coupon_ids: couponIds,
    ...(nextIsActive !== null && { is_active: nextIsActive }),
    ...(values.addTagIds.length > 0 && { add_tag_ids: values.addTagIds }),
    ...(values.removeTagIds.length > 0 && { remove_tag_ids: values.removeTagIds }),
  }
}
